/**
 * PublishDialog — the Publish-to-Embed dialog.
 *
 * Extracted from DashboardBuilderPage.js (iter-150). Renders the embed
 * token flow: choose an expiry, generate a token, and hand the user
 * copy-ready iframe + React SDK snippets. The parent owns the
 * open-state and passes in the callbacks it wants to run on publish +
 * copy (so this component stays presentation-only and testable).
 */
import React from 'react';
import { getBrandName } from '@/lib/branding';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

export function PublishDialog({
  open,
  onOpenChange,
  loadDashId,
  expiryHours,
  onExpiryChange,
  publishToken,
  busy,
  onGenerate,
  onCopy,
}) {
  // Snippet strings extracted once so the textarea value and the Copy
  // button share exactly the same content (avoids drift on future edits).
  const iframeSnippet = publishToken
    ? `<iframe src="${publishToken.embed_url}" width="100%" height="640" frameborder="0" sandbox="allow-scripts allow-same-origin"></iframe>`
    : '';
  const reactSnippet = publishToken
    ? `import { InzaraDashboard } from '@inzara/embed-sdk';\n\n<InzaraDashboard\n  baseUrl="${window.location.origin}"\n  token="${publishToken.embed_token}"\n  resourceId="${loadDashId}"\n  resourceType="dashboard"\n  height={640}\n/>`
    : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="publish-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">🚀 Publish to Embed</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <p className="text-[11px] text-muted-foreground/80 leading-relaxed">
            Generates a signed embed token for this dashboard. Anyone with the token can view — no {getBrandName()} login required.
            The token is scoped to this dashboard only and expires automatically.
          </p>
          <div className="flex items-center gap-2">
            <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Expires in</Label>
            <Select value={String(expiryHours)} onValueChange={v => onExpiryChange(Number(v))}>
              <SelectTrigger className="h-7 w-32 text-[10px]" data-testid="publish-expiry-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="24" className="text-xs">24 hours</SelectItem>
                <SelectItem value="168" className="text-xs">7 days</SelectItem>
                <SelectItem value="720" className="text-xs">30 days</SelectItem>
                <SelectItem value="8760" className="text-xs">1 year</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" onClick={onGenerate} disabled={busy} className="h-7 text-[10px]" data-testid="publish-generate-btn">
              {busy ? 'Generating…' : (publishToken ? 'Regenerate' : 'Generate token')}
            </Button>
          </div>
          {publishToken && (
            <div className="space-y-3 pt-2 border-t border-border/40">
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">Embed URL</Label>
                <div className="flex gap-1">
                  <Input readOnly value={publishToken.embed_url || ''} className="text-[10px] h-7 font-mono bg-muted/30" data-testid="publish-url-field" />
                  <Button size="sm" variant="outline" onClick={() => onCopy(publishToken.embed_url)} className="h-7 text-[10px]" data-testid="publish-copy-url">Copy</Button>
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">HTML iframe snippet</Label>
                <textarea
                  readOnly
                  className="w-full text-[10px] font-mono bg-muted/30 rounded p-2 border border-border/40 h-16 resize-none"
                  value={iframeSnippet}
                  data-testid="publish-iframe-snippet"
                />
                <Button size="sm" variant="outline" onClick={() => onCopy(iframeSnippet)} className="h-6 text-[10px] mt-1" data-testid="publish-copy-iframe">Copy iframe</Button>
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 block">React SDK snippet</Label>
                <textarea
                  readOnly
                  className="w-full text-[10px] font-mono bg-muted/30 rounded p-2 border border-border/40 h-24 resize-none"
                  value={reactSnippet}
                  data-testid="publish-react-snippet"
                />
                <Button size="sm" variant="outline" onClick={() => onCopy(reactSnippet)} className="h-6 text-[10px] mt-1" data-testid="publish-copy-react">Copy React</Button>
              </div>
              <p className="text-[10px] text-amber-400/80">
                ⚠ Anyone with this token can view the dashboard until {publishToken.expires_at}. Revoke from the Embeds page if needed.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-8">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PublishDialog;
